import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";
import { blogPosts, blogOrder, blogImages } from "@/data/blogPosts";
import { SITE_URL } from "@/data/servicePages";

const fmtDate = (iso) =>
  new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });

export default function BlogIndex() {
  useSEO({
    title: "Family Office Insights, Costs, Structures & Jurisdictions | Aurelius",
    description:
      "Practitioner guides on family office costs, SFO vs MFO decisions, DIFC and ADGM setup, governance and wealth migration, written by advisors who structure these offices.",
    canonical: `${SITE_URL}/insights`,
    ogTitle: "Family Office Insights | Aurelius",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Family Office Insights",
        url: `${SITE_URL}/insights`,
        hasPart: blogOrder.map((slug) => ({
          "@type": "Article",
          headline: blogPosts[slug].title,
          url: `${SITE_URL}/insights/${slug}`,
        })),
      },
    ],
  });
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <div className="bg-paper">
      <Navbar />
      <section className="bg-brand text-white">
        <div className="relative max-w-[1360px] mx-auto px-6 md:px-10 lg:px-12 pt-36 pb-16">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-white transition-colors mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> Aurelius Family Office
          </Link>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-200 mb-4">Insights</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.0] tracking-tight max-w-3xl">
            Practitioner guides, not marketing.
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">
            What family offices actually cost, how the structures actually work, and where wealth is actually moving, from the advisors who file the paperwork.
          </p>
        </div>
      </section>

      <section className="bg-paper py-20 md:py-28">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogOrder.map((slug, i) => {
              const p = blogPosts[slug];
              return (
                <Reveal key={slug} delay={i * 0.06}>
                  <Link
                    to={`/insights/${slug}`}
                    data-testid={`insight-card-${i}`}
                    className="group flex flex-col h-full"
                  >
                    <div className="rounded-2xl overflow-hidden mb-5">
                      <img src={blogImages[slug]} alt="" className="w-full h-52 object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                    </div>
                    <p className="text-[11px] uppercase tracking-widest text-brand mb-3">{p.category}</p>
                    <h2 className="font-display font-semibold text-xl text-ink mb-3 leading-snug group-hover:text-brand transition-colors">{p.title}</h2>
                    <p className="text-sm text-ink/55 leading-relaxed">{p.metaDescription}</p>
                    <div className="mt-5 flex items-center justify-between pt-4 border-t border-mist">
                      <p className="text-xs text-ink/40">{fmtDate(p.updated)} · {p.readTime} min read</p>
                      <ArrowUpRight className="w-5 h-5 text-ink/30 group-hover:text-brand group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
